import { getContainingWindow } from "@front/dom";
import { PeekyEmitter } from "@shared/peeky.emitter";
import { requestAnimationFramePromise } from "@shared/requestanimationframepromise";
import { sleep } from "@shared/sleep";
import { setValNoFocus, updatePerfectScroll } from "./editor";
import EditorElement from "./editorelement";
import { popupPane } from "./popuppane";

type Variant = {
	element?: EditorElement;
	id: string;
	name: string;
	weight?: number;
	style?: string;
	stretch?: string;
	woff?: string;
	woff2?: string;
}

export type Font = {
	element?: EditorElement;
	source: string;
	collection: string;
	id: string;
	name: string;
	variants: Variant[];
	defaultVariation?: string;
	disabled?: boolean;
};

type FontDescription = {
	string: string;
	source: string;
	family: string;
	font: Font;
	fontName: string;
	style: string;
	weight: number;
	variant: Variant;
	variantName: string;
	size: number;
	lineHeight: number;
	letterSpacing: number;
	disabled?: boolean;
}


let fontWeightName = {
	'100': 'Thin',
	'200': 'Extra Light',
	'300': 'Light',
	'400': 'Regular',
	'500': 'Medium',
	'600': 'Semi Bold',
	'700': 'Bold',
	'800': 'Extra Bold',
	'900': 'Black'
};
function getVariantName(variant: Variant) {
	if (variant.name) {
		return variant.name;
	}
	let name = '';
	name += fontWeightName[variant.weight] || variant.weight;
	if (variant.style !== 'normal') {
		name += ' ' + variant.style;
	}
	name = name.charAt(0).toUpperCase() + name.slice(1);
	return name;
};


export class Fontpicker extends PeekyEmitter {

	fonts: { [source: string]: Font[] } = {};
	nameIndex: { [key: string]: string } = {};
	fontIndex: { [key: string]: Font } = {};
	selectorIndex: { [key: string]: {
		css: string,
		url?: string
	}} = {};
	fontLoadIndex: { [url: string]: string } = {};
	style: HTMLStyleElement = document.createElement('style');
	requestedWeight?: number;
	requestedStyle?: string;
	requestedStretch?: string;

	private _awaitingAnimationFrame = false;
	private _ready = false;

	constructor(fontList: Font[], private site: EditorElement) {
		super();
		this.fonts.peeky = this.prepareFontList(fontList, 'peeky');
		(async () => {
			await this.reloadSiteFonts();
			this._ready = true;
			this.trigger('ready');
		})();
	}

	async ready() {
		if (this._ready) {
			return;
		}
		await new Promise((resolve) => {
			this.on('ready', resolve);
		});
	}

	async setCss(fontDescription: FontDescription, cssSelector: string | string[], mediaQuery?: string) {
		let style = this.style;
		let thisCss = '';
		if (fontDescription.family != null) thisCss += 'font-family:"' + fontDescription.family + '";\n';
		if (fontDescription.size != null) thisCss += 'font-size:' + fontDescription.size + '%;\n';
		if (fontDescription.weight != null) thisCss += 'font-weight:' + fontDescription.weight + ';\n';
		if (fontDescription.style != null) thisCss += 'font-style:' + fontDescription.style + ';\n';
		if (fontDescription.lineHeight != null) thisCss += 'line-height:' + fontDescription.lineHeight + '%;\n';
		if (fontDescription.letterSpacing != null) thisCss += 'letter-spacing:' + fontDescription.letterSpacing + 'em;\n';

		if (!Array.isArray(cssSelector)) {
			cssSelector = [cssSelector];
		}
		thisCss = cssSelector.join(',') + ' { ' + thisCss + ' } ';

		if (mediaQuery) {
			thisCss = mediaQuery + ' { ' + thisCss + ' } ';
		}

		// Load fonts when needed
		let loadCss = '';
		let loadUrl: string;

		// LOAD.
		let variant = fontDescription.variant;
		loadUrl = variant.woff2;
		if (loadUrl) {
			loadCss += '@font-face {\n';
			loadCss += 'font-family: ' + fontDescription.family + ';\n';
			loadCss += 'src: url("' + variant.woff2 + '") format("woff2");\n';
			loadCss += 'font-weight: ' + fontDescription.weight + ';\n';
			loadCss += 'font-style: ' + fontDescription.style + ';\n';
			loadCss += '}\n';
		}
		this.fontLoadIndex[loadUrl] = loadCss;

		this.selectorIndex[cssSelector.join()] = {
			css: thisCss,
			url: loadUrl
		}

		// Await for animation frame in case there are multiple calls at the same time
		if (!this._awaitingAnimationFrame) {
			this._awaitingAnimationFrame = true;
			await requestAnimationFramePromise(2);
			this._awaitingAnimationFrame = false;

			let iframe = <HTMLIFrameElement>document.getElementById('bodyframe');
			let iframeWindow = iframe.contentWindow;
			if (!iframeWindow) {
				return;
			}
			let iframeDocument = iframeWindow.document;

			let fullCss = '';
			let loadedUrl = {};
			for (let selector in this.selectorIndex) {
				let url = this.selectorIndex[selector].url;
				let css = this.selectorIndex[selector].css;
				if (url && !loadedUrl[url]) {
					fullCss += this.fontLoadIndex[url];
				}
				fullCss += css;
			}

			if (!getContainingWindow(style)) {
				style.innerHTML = fullCss;
				let head = iframeDocument.getElementsByTagName('head')[0];
				if (!head) {
					return;
				}
				head.appendChild(style);
			} else {
				style.innerHTML = fullCss;
			}

			let resizeEvent = iframeDocument.createEvent('HTMLEvents');
			resizeEvent.initEvent('resize', false, false);
			iframeDocument.dispatchEvent(resizeEvent);
		}
	}


	async reloadSiteFonts() {
		let site = this.site;
		let siteFonts: Font[] = [];
		this._ready = false;
		for (let fontRelation of site.related.font || []) {
			let fontElement = fontRelation.element;
			await fontElement.fetch();
			let font: Font = {
				element: fontElement,
				source: fontElement.data('source'),
				collection: 'site',
				id: fontElement.data('id'),
				name: fontElement.data('name'),
				variants: []
			};
			for (let variantRelation of fontElement.related.variant || []) {
				let variantElement = variantRelation.element;
				let variant: Variant = {
					element: variantElement,
					id: variantElement.data('id'),
					name: variantElement.data('name'),
					weight: variantElement.data('weight'),
					style: variantElement.data('style'),
					woff2: variantElement.data('url'),
					stretch: variantElement.data('stretch')
				};
				font.variants.push(variant);
			}
			siteFonts.push(font);
		}
		this.fonts.site = this.prepareFontList(siteFonts, 'site');
		this._ready = true;
		this.trigger('ready');
	}


	prepareFontList(fontList: Font[], collectionName: string) {
		// Sort fonts
		fontList.sort((a: Font, b: Font) => {
			if (a.name > b.name) {
				return 1;
			} else if (a.name < b.name) {
				return -1;
			}
			return 0;
		});
	
		for (let i=0; i<fontList.length; i++) {
			let font = fontList[i];
			if (!font.id) {
				fontList = fontList.splice(i--, 1);
				continue;
			}
			font.source = font.source.replace(/-.*/, ''); // Remove old redundant uuid from peeky- fonts
			font.id = font.id.toLowerCase();
			font.collection = collectionName;
			this.nameIndex[font.id] = font.name;
			this.fontIndex[font.source + ':' + font.id] = font;
	
			// Sort each fonts variants
			font.variants.sort((a, b) => {
				if (a.weight > b.weight) return 1;
				if (a.weight < b.weight) return -1;
				if (a.style < b.style) return 1;
				if (a.style > b.style) return -1;
				return 0;
			});
		}

		return fontList;
	}


	async resolve(input: string | string[]): Promise<FontDescription> {
		await this.ready();

		let fontArray: string[];

		if (Array.isArray(input)) {
			fontArray = input;
		} else {
			fontArray = input.split(':');
		}

		let source = fontArray[0] || 'native';
		let family = (fontArray[1] || 'arial').toLowerCase();
		let wantedStyle: string = fontArray[2] || 'normal';
		let wantedWeight: number = parseFloat(fontArray[3] || '400');
		let size = fontArray[4] || '100';
		let lineHeight = fontArray[5] || '120';
		let letterSpacing = fontArray[6] || '0';
		source = source.replace(/-.*/, ''); // Remove old redundant uuid

		let font: Font = this.fontIndex[source + ':' + family] || {
			collection: '',
			source: '',
			id: '',
			name: '',
			variants: [],
			disabled: true
		};
		let variant = this.getClosestVariant({ weight: wantedWeight, style: wantedStyle }, font)
		if (!variant) {
			variant = {
				id: '',
				name: 'The selected font is not available'
			};
		}

		let name = font.name.charAt(0).toUpperCase() + font.name.slice(1);
		return {
			string: fontArray.join(':'),
			source: source,
			family: family,
			font: font,
			fontName: name,
			style: variant.style,
			weight: variant.weight,
			variant: variant,
			variantName: getVariantName(variant),
			size: parseFloat(size),
			lineHeight: parseFloat(lineHeight),
			letterSpacing: parseFloat(letterSpacing)
		};
	};

	getClosestVariant(wss: { weight: number, style: string, stretch?: string }, font: Font) {
		let variants = font.variants;

		// Look for closest match
		let foundWeight: number | undefined;
		let foundStyle: string;
		let foundVariant: Variant;

		for (let i = 0; i < variants.length; i++) {
			let variant = variants[i];
			let checkWeight = variant.weight;
			let checkStyle = variant.style;
			if (typeof (foundWeight) === 'undefined') {
				foundWeight = checkWeight;
				foundStyle = checkStyle;
				foundVariant = variant;
			} else if ((foundStyle !== wss.style || checkStyle === wss.style) && Math.abs(checkWeight - wss.weight) <= Math.abs(foundWeight - wss.weight)) {
				foundWeight = checkWeight;
				foundStyle = checkStyle;
				foundVariant = variant;
			}
		}
		return foundVariant;
	}



	async activate(fontInput: string | string[], cssSelector: string, mediaQuery?: string) {
		await this.ready();

		let fontDescription: FontDescription;

		if (Array.isArray(fontInput)) {
			fontDescription = await this.resolve(fontInput);
		}
		else if (typeof (fontInput) === 'string') {
			fontDescription = await this.resolve(fontInput.split(':'));
		}
		else if (typeof (fontInput) === 'object') {
			fontDescription = fontInput;
		}

		if (!fontDescription || fontDescription.disabled) {
			return;
		}

		await this.setCss(fontDescription, cssSelector, mediaQuery);
	}


	async popup(options, communicator) {
		let $fontSelector = $('<div class="fontpicker"></div>');
		let $familyList = $('<div class="familyList"></div>');
		let $fontVariant = $('<div class="fontVariation"></div>');
		let $active: JQuery;

		let fontIndex = this.fontIndex;
		let nameIndex = this.nameIndex;

		// Font properties
		let $fontProperties = $('<div class="fontProperties"></div>');
		let $fontSize = $('<span class="floatLeft fontpropLabelWrap first"><label class="fontpropLabel textfield"><span class="sprite inlineblock _fontSize"></span><input type="text" class="text number inline center">%</label></span>');
		let $lineHeight = $('<span class="floatLeft fontpropLabelWrap"><label class="fontpropLabel textfield"><span class="sprite inlineblock _lineHeight"></span><input type="text" class="text number inline center">%</label></span>');
		let $letterSpacing = $('<span class="floatLeft fontpropLabelWrap last"><label class="fontpropLabel textfield"><span class="sprite inlineblock _letterSpacing"></span><input type="text" class="text number float inline center">em</label></span>');
		$fontProperties.append($fontSize);
		$fontProperties.append($lineHeight);
		$fontProperties.append($letterSpacing);


		// Family list
		$fontSelector.append($familyList);

		// Add new
		$fontSelector.append(
			'<div class="fontpickerAddNew">' + 
			'<div class="label">+ Add fonts from Adobe Fonts&hellip;</div>' +
			'<div class="typekit fontpickerAddThirdParty">' +
				'<div class="fontpickerFlex">' +
				'<input  pattern="[A-Za-z0-9]+" class="text" type="text" placeholder="Adobe Fonts project ID"><button class="add">+</button>' +
				'</div>' +
			'</div>' +
			'</div>'
		);

		// Variation-list
		$fontSelector.append($fontVariant);

		// Properties
		$fontSelector.append($fontProperties);

		// Cancel / select
		$fontSelector.append(
			'<div class="actionbuttons">' +
			'<button class="cancel">Cancel</button>' +
			'<button class="select">Select</button>' +
			'</div>'
		);


		// Render font list
		let $domIndex = {};
		let originalFont = options.value; //this.currentFont;
		let defaultFont = options.placeholder; //this.defaultFont;
		let cssSelector = options.cssSelector;
		let mediaQuery = options.mediaQuery;
		let selectedFont = (originalFont || defaultFont || 'native:Arial:normal:400:100:120:0').split(':');

		let renderList = async (collectionName: string, label?: string) => {
			await this.ready() 

			let fontList = this.fonts[collectionName] || [];
			let $container = $familyList.find('.fontpickerCollection.fontpickerCollectionName-' + collectionName);
			if ($container.length > 0) {
				$container.html('<div class="fontpickerCollectionLabel">' + label + '</div>');
			} else {
				$container = $('<div class="fontpickerCollection fontpickerCollectionName-' + collectionName + '"></div>');
				$container.append('<div class="fontpickerCollectionLabel">' + label + '</div>');
				$familyList.append($container);
			}
			if (fontList.length <= 0) {
				$container.html('');
				return;
			}
			for (let font of fontList) {
				let $font = $(
					'<div class="fontFamily" data-id="' + font.id + '" data-source="' + font.source + '">' +
					'<div class="fontName">' + font.name + '</div>' +
					(collectionName === 'site' ? '<div class="fontRemove">×</div>' : '') +
					'</div>'
				);
				if (font.element) {
					$font.data('element', font.element);
				}
				$container.append($font);
				$domIndex[font.source + ':' + font.id] = $font;
			}
		}

		await renderList('site', 'Your fonts');
		await renderList('peeky', 'Platform fonts');

		let buildVariationList = (fontDescription: FontDescription) => {
			let font = fontIndex[fontDescription.source + ':' + fontDescription.family];
			if (!font) {
				return;
			}

			let variantListContent = '<div class="selectedVariation dropdownheader"></div>';
			variantListContent += '<div class="variationList">';
			for (let i=0; i<font.variants.length; i++) {
				let variant = font.variants[i];
				variantListContent += '<div class="variation" data-weight="' + variant.weight + '" data-style="' + variant.style + '" data-variation="' + variant.name + '">';
				//variantListContent += (variant.name || (variant.style + ' ' + variant.weight)).replace(new RegExp("^" + nameIndex[fontDescription[1]] + ' '), '');
				variantListContent += getVariantName(variant);
				variantListContent += '</div>';
			}
			variantListContent += '</div>';
			$fontVariant.html(variantListContent);
		}


		let markActiveFamily = (font) => {
			let $dom = $domIndex[font.source + ':' + font.family];
			if ($dom) {
				$fontSelector.find('.active').removeClass('active');
				$dom.addClass('active');
				$active = $dom;

				// Scroll if needed
				let $scrollpane = $dom.closest('.familyList');
				let scrollTop = $scrollpane.scrollTop();
				let scrollHeight = $scrollpane.height();
				let itemHeight = $dom.height();
				let top = $dom.offset().top - $scrollpane.offset().top;
				if (top < 0) {
					$scrollpane.scrollTop(scrollTop+top);
				} else if ((top+itemHeight) > scrollHeight) {
					$scrollpane.scrollTop(scrollTop + (top+itemHeight-scrollHeight));
				}
			}
		};


		let markActiveVariant = (font) => {
			let $activeVariation: JQuery;
			let $fontVariations = $fontVariant.find('.variation');
			for (let i = 0; i < $fontVariations.length; i++) {
				let $this = $($fontVariations[i]);
				if ($this.data('weight') == font.weight && $this.data('style') === font.style) {
					$activeVariation = $this;
				}
			}
			$fontVariant.find('.active').removeClass('active');
			if ($activeVariation) {
				$activeVariation.addClass('active');
				$fontVariant.find('.selectedVariation').html($activeVariation.html());

				// Scroll if needed
				let $scrollpane = $activeVariation.closest('.variationList');
				let scrollTop = $scrollpane.scrollTop();
				let scrollHeight = $scrollpane.height();
				let itemHeight = $activeVariation.height();
				let top = $activeVariation.offset().top - $scrollpane.offset().top;
				if (top < 0) {
					$scrollpane.scrollTop(scrollTop+top);
				} else if ((top+itemHeight) > scrollHeight) {
					$scrollpane.scrollTop(scrollTop + (top+itemHeight-scrollHeight));
				}
			}
		}


		let openFontPicker = () => {
			if (!$familyList.is('.ps')) {
				updatePerfectScroll($familyList);
			}
			updateScrollPosition();
			$(document).on('keydown', keypress);
		}


		let removeFontPicker = () => {
			$fontSelector.off('close', selectFont);
			$fontSelector.trigger('close');
			$(document).off('keydown', keypress);
		}


		let selectFont = async () => {
			communicator({
				font: await this.resolve(selectedFont),
				kill: true
			});
			removeFontPicker();
		};


		// Set initial values
		let resolvedFont = await this.resolve(selectedFont);
		this.requestedWeight = resolvedFont.weight;
		this.requestedStyle = resolvedFont.style;
		setValNoFocus($fontSize.find('input'), resolvedFont.size.toString());
		setValNoFocus($lineHeight.find('input'), resolvedFont.lineHeight.toString());
		setValNoFocus($letterSpacing.find('input'), resolvedFont.letterSpacing.toString());
		buildVariationList(resolvedFont);
		markActiveFamily(resolvedFont);
		markActiveVariant(resolvedFont);

		$fontSelector.on('initPane', openFontPicker);
		$fontSelector.on('close', selectFont);


		let cancel = async () => {
			let font = originalFont || defaultFont;
			this.activate(font.split(':'), cssSelector, mediaQuery);
			communicator({
				label: await this.resolve(font),
				kill: true
			});
			removeFontPicker();
		};


		let keypress = (e?) => {
			if (e && e.target && e.target.tagName.toLowerCase() === 'input') {
				return; // Make input fields behave as usual
			}
			let variant = $fontVariant.is('.dropped');
			let $thisActive = variant ? $fontVariant.find('.active') : $active;
			let $all = variant ? $fontVariant.find('.variant') : $fontSelector.find('.fontFamily');
			if (e.which === 38) {
				if (variant) {
					$thisActive.prev().trigger('click');
				} else {
					let index = $all.index($active);
					let $prev = $($all.get(index-1));
					$prev.find('.fontName').trigger('click');
				}
				return false;
			} else if (e.which === 40) {
				if (variant) {
					$thisActive.next().trigger('click');
				} else {
					let index = $all.index($active);
					let $prev = $($all.get(index+1));
					$prev.find('.fontName').trigger('click');
				}
				return false;
			} else if (e.which === 13) {
				selectFont();
				return false;
			} else if (e.which === 27) {
				cancel();
				return false;
			}
		}


		let closeVariationList = (e?) => {
			if ($fontVariant.is('.dropped')) {
				let $list = $fontVariant.find('.variationList');
				$list.filter(':visible').slideUp(130);
				$fontVariant.removeClass('dropped');
			}
		};


		let openVariationList = async (e?) => {
			let $list = $fontVariant.find('.variationList');
			$list.css('height', 'auto');
			$list.width();
			let listHeight = $list.outerHeight();
			let variantOffset = $fontVariant.offset();
			let variantHeight = $fontVariant.outerHeight();
			let selectorOffset = $fontSelector.offset();
			let selectorHeight = $fontSelector.outerHeight();
			let spaceAbove = variantOffset.top - selectorOffset.top;
			let spaceBelow = (selectorOffset.top + selectorHeight) - (variantOffset.top + variantHeight);

			if (listHeight > spaceBelow) {
				$list.addClass('above');
			} else {
				$list.removeClass('above');
			}
			if (listHeight > spaceAbove) {
				$list.css('height', spaceAbove - 10);
			}

			$list.filter(':hidden').slideDown(150);
			$fontVariant.addClass('dropped');

			await sleep(1);
			updatePerfectScroll($list);
			$(document).one('click', function maybeCloseVariationList(e) {
				// Clicked inside font-variation
				if ($(e.target).parents().addBack().filter($fontVariant).length > 0) {
					$(document).one('click', maybeCloseVariationList);
				} else {
					closeVariationList();
				}
			});
		};


		// Scroll to correct position in font list
		let $scrollpane = $familyList.children();
		let updateScrollPosition = (smooth?) => {
			$active = $active || $familyList.find('.active');
			if (!$active || $active.length <= 0) return;
			let activeTop = $scrollpane.scrollTop() + $active.position().top;
			let activeBottom = activeTop + $active.outerHeight();
			let listHeight = $familyList.height();
			let listTop = $scrollpane.scrollTop();
			let listBottom = listTop + listHeight;
			let scrollTop;
			if (activeTop >= listTop && activeBottom <= listBottom) return; // All OK
			else if (activeTop < listTop) scrollTop = activeTop;
			else scrollTop = activeBottom - listHeight;
			if (smooth) $scrollpane.stop().animate({ scrollTop: scrollTop }, 100);
			else $scrollpane.stop().scrollTop(scrollTop);
		};


		// On selection
		let picker = this;
		$familyList.on('click', '.fontFamily .fontName', async function () {
			let $fontFamily = $(this).closest('.fontFamily');
			let id = $fontFamily.data('id');
			let weight = picker.requestedWeight || 400;
			let style = picker.requestedStyle || 'normal';
			let stretch = picker.requestedStretch || 'normal';
			selectedFont[0] = $fontFamily.data('source');
			selectedFont[1] = id;
			selectedFont[2] = style;
			selectedFont[3] = weight;
			let resolvedFont = await picker.resolve(selectedFont);
			buildVariationList(resolvedFont);
			picker.activate(selectedFont, cssSelector, mediaQuery);
			markActiveFamily(resolvedFont);
			markActiveVariant(resolvedFont);

			// Don't save the font yet, just update the label
			communicator({
				label: await picker.resolve(selectedFont)
			});
		});

		$fontVariant.on('click', '.selectedVariation, .variation.active', function () {
			if ($fontVariant.is('.dropped')) {
				closeVariationList();
			} else {
				openVariationList();
			}
		});

		$fontVariant.on('click', '.variation', async function () {
			let $variant = $(this);
			let weight = $variant.data('weight');
			let style = $variant.data('style');
			selectedFont[2] = style;
			selectedFont[3] = weight;
			picker.requestedWeight = weight;
			picker.requestedStyle = style;
			picker.activate(selectedFont, cssSelector, mediaQuery);
			markActiveVariant(await picker.resolve(selectedFont));
		});
		$fontSize.on('change keyup', function () {
			selectedFont[4] = $(this).find('input').val();
			picker.activate(selectedFont, cssSelector, mediaQuery);
		});
		$lineHeight.on('change keyup', function () {
			selectedFont[5] = $(this).find('input').val();
			picker.activate(selectedFont, cssSelector, mediaQuery);
		});
		$letterSpacing.on('change keyup', function () {
			selectedFont[6] = $(this).find('input').val();
			picker.activate(selectedFont, cssSelector, mediaQuery);
		});

		$fontSelector.on('click', '.cancel', cancel);
		$fontSelector.on('click', '.select', selectFont);
		$fontSelector.on('initPane', function () {
			updateScrollPosition();
		});

		$fontSelector.on('click', 'button.add', function() {
			let $button = $(this);
			let $input = $button.parent().find('input:text');
			let key = $input.val().toString();
			// Check if font is already added
			let site = picker.site;
			site.submit('entity', site.entity);
			site.submit('_id', site);
			site.submit('insert.importTypekit', key);
			$button.addClass('fontpickerLoading');
			site.editor.once('submitted', (param, data) => {
				$button.removeClass('fontpickerLoading');
				// Update UI
				if (!data?.global?._add) {
					return;
				}
				site.editor.once('fetched', async () => {
					await picker.reloadSiteFonts();
					await renderList('site', 'Your fonts');
					$input.val('');
					$button.closest('.fontpickerAddThirdParty').slideUp(250);
				});
			});
		});

		// Remove
		$familyList.on('click', '.fontFamily .fontRemove', function () {
			let $fontFamily = $(this).closest('.fontFamily');
			let element = $fontFamily.data('element');
			if (element) {
				picker.site.unrelate(element, 'font', picker.site);
				picker.reloadSiteFonts();
				renderList('site', 'Your fonts');
			}
			return false;
		});

		// Add new dropdown
		$fontSelector.on('click', '.fontpickerAddNew .label', () => {
			$fontSelector.find('.fontpickerAddThirdParty').slideToggle(250);
		});

		let $container = options.$container || $('body');
		let $trigger = options.$trigger || $container;

		popupPane($fontSelector, $container, $trigger);
	};
}